import {
  Users,
  UserCheck,
  ScanLine,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Building2,
  CheckCircle2,
  Clock,
  AlertOctagon,
  Activity,
  Package
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

export type StatCardIcon = 'users' | 'members' | 'checkins' | 'revenue' | 'gym' | 'active' | 'expiring' | 'expired' | 'activity' | 'inventory';
export type StatCardColor = 'primary' | 'success' | 'warning' | 'destructive' | 'accent';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  icon: StatCardIcon;
  format?: 'number' | 'currency' | 'percentage';
  color?: StatCardColor;
  trendLabel?: string;
}

const iconMap = {
  users: Users,
  members: UserCheck,
  checkins: ScanLine,
  revenue: DollarSign,
  gym: Building2,
  active: CheckCircle2,
  expiring: Clock,
  expired: AlertOctagon,
  activity: Activity,
  inventory: Package,
};

const colorMap = {
  primary: 'text-primary bg-primary/10',
  success: 'text-success bg-success/10',
  warning: 'text-warning bg-warning/10',
  destructive: 'text-destructive bg-destructive/10',
  accent: 'text-accent-foreground bg-accent',
};

const defaultColorMap: Record<StatCardIcon, StatCardColor> = {
  users: 'primary',
  members: 'success',
  checkins: 'warning',
  revenue: 'accent',
  gym: 'primary',
  active: 'success',
  expiring: 'warning',
  expired: 'destructive',
  activity: 'primary',
  inventory: 'warning',
};

export function StatCard({ title, value, change, icon, format = 'number', color, trendLabel = 'vs mes anterior' }: StatCardProps) {
  const Icon = iconMap[icon];
  const colorKey = color || defaultColorMap[icon];
  const colorClass = colorMap[colorKey];

  const formatValue = (val: string | number) => {
    if (typeof val === 'string') return val;
    if (format === 'currency') return `$${val.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`;
    if (format === 'percentage') return `${val}%`;
    return val.toLocaleString('es-ES');
  };

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow duration-200">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-3xl font-bold text-foreground">{formatValue(value)}</p>

            {change !== undefined && (
              <div className={cn(
                "flex items-center gap-1 text-sm font-medium",
                change >= 0 ? "text-success" : "text-destructive"
              )}>
                {change >= 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span>{Math.abs(change)}%</span>
                <span className="text-muted-foreground font-normal">{trendLabel}</span>
              </div>
            )}
          </div>

          <div className={cn("rounded-xl p-3", colorClass)}>
            <Icon className="h-6 w-6" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
